import type { JiraProjectSummary } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { ProjectCreateError } from '@/libraries/bxp-web-stargate-client/src/util/unsuccessful-fetch-error';

const JIRA_PROJECT_RECOVERY_POLL_INTERVAL_MS = 5_000;
const JIRA_PROJECT_RECOVERY_TIMEOUT_MS = 120_000;

export type ProjectRecoveryStrategy = 'lookup-once' | 'poll';

export const getProjectRecoveryStrategy = (error: unknown): ProjectRecoveryStrategy => {
	if (!(error instanceof ProjectCreateError)) {
		return error instanceof TypeError ||
			(error instanceof Error && /timed?\s*out|timeout/i.test(error.message))
			? 'poll'
			: 'lookup-once';
	}

	const { status } = error;
	return status === 408 || status === 409 || status >= 500 ? 'poll' : 'lookup-once';
};

type RecoverJsmUseCaseProjectOptions = {
	readonly getProject: () => Promise<JiraProjectSummary | undefined>;
	readonly isCancelled: () => boolean;
	readonly strategy: ProjectRecoveryStrategy;
	readonly waitForNextCheck: (delay: number) => Promise<void>;
};

export const recoverJsmUseCaseProject = async ({
	getProject,
	isCancelled,
	strategy,
	waitForNextCheck,
}: RecoverJsmUseCaseProjectOptions): Promise<JiraProjectSummary | undefined> => {
	const deadline = Date.now() + JIRA_PROJECT_RECOVERY_TIMEOUT_MS;

	while (!isCancelled()) {
		try {
			const project = await getProject();
			if (project) {
				return project;
			}
		} catch {
			// Ambiguous failures can coincide with Jira startup. Retry lookup while polling.
		}

		if (strategy === 'lookup-once' || isCancelled()) {
			return undefined;
		}

		const remainingTime = deadline - Date.now();
		if (remainingTime <= 0) {
			return undefined;
		}

		await waitForNextCheck(Math.min(JIRA_PROJECT_RECOVERY_POLL_INTERVAL_MS, remainingTime));
	}

	return undefined;
};
